/**
 * ============================================================
 * MORNING FIX COFFEE
 * BUILD #8 — PRODUCTION CONTROLLER
 * VERSION 1.1.0
 * ============================================================
 *
 * RESPONSIBILITY
 * ------------------------------------------------------------
 * Build #8 is the production entry / integration layer.
 *
 * BUILD #1 = Profile Engine
 * BUILD #2 = Landing Page Router
 * BUILD #3 = Product Resolution
 * BUILD #4 = Shopify Cart
 * BUILD #5 = Subscription Experience
 * BUILD #6 = Customer-Facing UI
 * BUILD #7 = Orchestrator
 * BUILD #8 = Production Controller
 *
 * Build #8 receives the completed profile and hands it to
 * Build #7. It does not score, route, resolve products, render
 * UI, or perform cart/subscription operations.
 *
 * ============================================================
 */

(function (window, document) {

  'use strict';

  const VERSION = '1.1.0';
  const BUILD = 8;

  const DEFAULT_CONFIG = {
    containerId: 'coffee-taste-profile',
    errorEvent: 'coffeeProfileError'
  };

  const state = {
    initialized: false,
    completed: false,
    destroyed: false,
    busy: false,
    profile: null,
    container: null,
    config: null
  };

  function mergeConfig(config) {
    return Object.assign({}, DEFAULT_CONFIG, config || {});
  }

  function getOrchestrator() {

    if (!window.CoffeeTasteProfileOrchestrator) {
      throw new Error(
        'Build #8 requires Build #7: CoffeeTasteProfileOrchestrator.'
      );
    }

    if (
      typeof window.CoffeeTasteProfileOrchestrator.start !==
      'function'
    ) {
      throw new Error(
        'Build #7 start() is unavailable.'
      );
    }

    return window.CoffeeTasteProfileOrchestrator;
  }

  function resolveContainer(container) {

    if (container && typeof container === 'string') {
      container = document.querySelector(container);
    }

    if (container && container.nodeType) {
      return container;
    }

    if (state.config && state.config.containerId) {
      const found = document.getElementById(
        state.config.containerId
      );

      if (found) {
        return found;
      }
    }

    throw new Error(
      'Build #8 production container not found.'
    );
  }

  function validateProfile(profile) {

    if (!profile) {
      throw new Error(
        'Build #8 requires a completed coffee profile.'
      );
    }

    if (profile.complete !== true) {
      throw new Error(
        'Build #8 requires profile.complete === true.'
      );
    }

    if (!profile.roastResult) {
      throw new Error(
        'Build #8 completed profile is missing roastResult.'
      );
    }

    if (!profile.roastResult.id) {
      throw new Error(
        'Build #8 profile is missing roastResult.id.'
      );
    }

    return true;
  }

  function emitError(error) {

    if (
      !document ||
      typeof document.dispatchEvent !== 'function'
    ) {
      return;
    }

    const eventName =
      state.config && state.config.errorEvent
        ? state.config.errorEvent
        : DEFAULT_CONFIG.errorEvent;

    let event;

    if (typeof CustomEvent === 'function') {

      event = new CustomEvent(
        eventName,
        {
          detail: {
            error: error,
            build: BUILD,
            version: VERSION
          }
        }
      );

    } else {

      event = document.createEvent('CustomEvent');

      event.initCustomEvent(
        eventName,
        false,
        false,
        {
          error: error,
          build: BUILD,
          version: VERSION
        }
      );
    }

    document.dispatchEvent(event);
  }

  function complete(profile) {

    validateProfile(profile);

    if (state.busy) {
      return state.profile;
    }

    /*
     * Once a completed profile has successfully entered the
     * production experience, do not launch it a second time.
     */
    if (state.completed) {
      if (state.profile === profile) {
        return state.profile;
      }

      throw new Error(
        'Build #8 has already completed a coffee profile. ' +
        'Call reset() or destroy() before starting another.'
      );
    }

    state.busy = true;

    try {

      const orchestrator = getOrchestrator();

      const result = orchestrator.start(
        profile,
        state.container,
        state.config
      );

      state.profile = profile;
      state.completed = true;
      state.initialized = true;
      state.destroyed = false;

      return result;

    } catch (error) {

      emitError(error);
      throw error;

    } finally {

      state.busy = false;
    }
  }

  function start(options) {

    options = options || {};

    /*
     * If already initialized, a supplied profile is treated as
     * an explicit completion handoff. Otherwise remain idempotent.
     */
    if (
      state.initialized &&
      !state.destroyed
    ) {

      if (options.profile) {
        return complete(options.profile);
      }

      return API;
    }

    /*
     * Validate Build #7 before mutating production state.
     * This prevents Build #8 from entering an apparently
     * initialized state when its required orchestrator is absent.
     */
    getOrchestrator();

    state.config = mergeConfig(options.config);
    state.container = resolveContainer(options.container);
    state.destroyed = false;
    state.initialized = true;
    state.completed = false;
    state.profile = null;

    if (options.profile) {
      return complete(options.profile);
    }

    return API;
  }

  function startWithProfile(
    profile,
    container,
    config
  ) {

    validateProfile(profile);

    if (!state.initialized || state.destroyed) {
      start({
        container: container,
        config: config
      });
    }

    return complete(profile);
  }

  function reset() {

    if (
      window.CoffeeTasteProfileOrchestrator &&
      typeof window.CoffeeTasteProfileOrchestrator.destroy ===
      'function'
    ) {
      window.CoffeeTasteProfileOrchestrator.destroy();
    }

    state.profile = null;
    state.completed = false;
    state.busy = false;
    state.destroyed = false;

    return true;
  }

  function destroy() {

    reset();

    state.initialized = false;
    state.destroyed = true;
    state.container = null;
    state.config = null;

    return true;
  }

  function getState() {

    return {
      version: VERSION,
      build: BUILD,
      initialized: state.initialized,
      completed: state.completed,
      busy: state.busy,
      destroyed: state.destroyed,
      hasProfile: !!state.profile,
      resultId:
        state.profile &&
        state.profile.roastResult
          ? state.profile.roastResult.id
          : null
    };
  }

  function validate() {

    const errors = [];

    if (typeof window === 'undefined') {
      errors.push('Browser window is unavailable.');
    }

    if (typeof document === 'undefined') {
      errors.push('Browser document is unavailable.');
    }

    if (
      typeof window !== 'undefined' &&
      !window.CoffeeTasteProfileOrchestrator
    ) {
      errors.push(
        'Build #7 CoffeeTasteProfileOrchestrator is unavailable.'
      );

    } else if (
      typeof window !== 'undefined' &&
      typeof window.CoffeeTasteProfileOrchestrator.start !==
      'function'
    ) {
      errors.push(
        'Build #7 start() is unavailable.'
      );
    }

    return {
      valid: errors.length === 0,
      version: VERSION,
      build: BUILD,
      dependencies: {
        build7:
          typeof window !== 'undefined' &&
          !!window.CoffeeTasteProfileOrchestrator,
        build7Start:
          typeof window !== 'undefined' &&
          !!window.CoffeeTasteProfileOrchestrator &&
          typeof window.CoffeeTasteProfileOrchestrator.start ===
          'function'
      },
      containerAvailable:
        typeof document !== 'undefined' &&
        !!document.getElementById(
          DEFAULT_CONFIG.containerId
        ),
      errors: errors
    };
  }

  const API = {
    version: VERSION,
    build: BUILD,
    start,
    startWithProfile,
    complete,
    reset,
    destroy,
    getState,
    validate
  };

  if (typeof window !== 'undefined') {
    window.CoffeeProductionController = API;
  }

})(window, document);
